/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/utils/leaveWorkingDays.ts
 * PURPOSE: Client-side mirror of the server's `_count_working_days()` formula
 *          (BS-024-R08) — used ONLY to render the "Số ngày phép sẽ trừ" preview
 *          [11] before submit (BS-024-R09). The server is the sole authority:
 *          this value is NEVER sent to the server, only displayed.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-24
 * COMPLIANCE: SDLC 6.5.0 + BS-024-R08/R09
 * PILLAR: P1 (Stage 06 - BUILD)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Vietnamese Leave Management — working-days-to-deduct preview
 * - Impact: Wrong preview here is cosmetic (server recomputes authoritatively
 *   on submit) but a wrong/misleading preview erodes trust in the form
 * - Stakeholders: Employees (preview), @ba (BS-024-R08/R09 owner)
 *
 * @technical-architecture
 * - Layer: Pure utility (no I/O, no React) — unit-testable in isolation
 * - Pattern: mirrors backend/app/api/v1/leave_request.py::_count_working_days()
 *   day-by-day: skip Sat(6)/Sun(0), skip any date inside a holiday's
 *   [start_date, end_date ?? start_date] inclusive range
 *
 * @cross-references
 * - Design: docs/01-planning/02-uat-len-lich-cham-cong/BS-024-PHAN-DOAN-D-NHAN-VIEN-NGHI-PHEP.md §BS-024-R08/R09
 * - Server SSOT (must stay in sync): backend/app/api/v1/leave_request.py _count_working_days()
 * - Consumer: frontend/src/pages/LeaveRequestModern.tsx
 * - Tests: frontend/tests/utils/leaveWorkingDays.test.ts
 */

import type { PublicHolidayDTO } from '../services/api/leaveRequest';

export interface LeavePreview {
  /** Ngày trong khoảng, trừ T7/CN + ngày lễ — số ngày phép sẽ bị trừ thật. */
  workingDays: number;
  /** Số ngày lễ (rơi vào ngày thường) nằm trong khoảng — hiển thị trong ngoặc. */
  holidayCount: number;
}

/** Parse a strict YYYY-MM-DD string into a local Date at midnight, or null. */
function parseIsoDate(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return date;
}

function toKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * BS-024-R08/R09: count business days in [start, end] inclusive, excluding
 * Sat/Sun and any tenant-configured public holiday date. `holidayCount` is
 * how many of the excluded days were holidays landing on what would
 * otherwise have been a weekday (matches server semantics: a holiday that
 * falls on a weekend was never going to be counted anyway, so it must not
 * inflate the "k ngày lễ không trừ vào phép" figure).
 *
 * Returns {workingDays: 0, holidayCount: 0} for invalid/empty input or
 * end < start (mirrors server's `if end < start: return 0`).
 */
export function computeLeavePreview(
  start: string,
  end: string,
  holidays: PublicHolidayDTO[]
): LeavePreview {
  const startDate = parseIsoDate(start);
  const endDate = parseIsoDate(end);
  if (!startDate || !endDate || endDate < startDate) {
    return { workingDays: 0, holidayCount: 0 };
  }

  const holidayDates = new Set<string>();
  for (const h of holidays) {
    const hStart = parseIsoDate(h.start_date);
    if (!hStart) continue;
    const hEnd = h.end_date ? parseIsoDate(h.end_date) ?? hStart : hStart;
    const d = new Date(hStart);
    while (d <= hEnd) {
      holidayDates.add(toKey(d));
      d.setDate(d.getDate() + 1);
    }
  }

  let workingDays = 0;
  let holidayCount = 0;
  const current = new Date(startDate);
  while (current <= endDate) {
    const isWeekend = current.getDay() === 0 || current.getDay() === 6;
    const isHoliday = holidayDates.has(toKey(current));
    if (!isWeekend && !isHoliday) {
      workingDays += 1;
    } else if (!isWeekend && isHoliday) {
      holidayCount += 1;
    }
    current.setDate(current.getDate() + 1);
  }

  return { workingDays, holidayCount };
}

/** BS-024-R09 label: "{N} ngày (trong đó {k} ngày lễ không trừ vào phép)", k=0 → bỏ ngoặc. */
export function formatLeavePreview(preview: LeavePreview): string {
  const { workingDays, holidayCount } = preview;
  if (holidayCount === 0) {
    return `${workingDays} ngày`;
  }
  return `${workingDays} ngày (trong đó ${holidayCount} ngày lễ không trừ vào phép)`;
}
